import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, MapPin, Clock, Phone, MessageCircle, Plus, Minus, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SITE, breadcrumbSchema, faqSchema, localBusinessSchema } from "@/lib/seo";
import { trackContact } from "@/lib/analytics";
import { getLanding } from "@/lib/landingPages";

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E5E5DF]">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`landing-faq-${idx}`}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
      >
        <span className="font-body text-base text-[#1A1A1A]">{q}</span>
        {open ? <Minus size={18} className="text-[#B84A3B] flex-shrink-0" /> : <Plus size={18} className="text-[#B84A3B] flex-shrink-0" />}
      </button>
      {open && <p className="pb-5 text-sm text-[#6E6E69] leading-relaxed max-w-2xl">{a}</p>}
    </div>
  );
};

const StoreCard = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-[#1A1A1A] text-[#FFFFFF] p-8 sm:p-10" data-testid="landing-store-card">
    <div>
      <div className="overline text-[#B84A3B] mb-3">Visit Our Boutique</div>
      <div className="flex items-start gap-3 mb-4">
        <MapPin size={18} className="text-[#B84A3B] mt-0.5 flex-shrink-0" />
        <p className="text-sm leading-relaxed">{SITE.address.street},<br />{SITE.address.city}, {SITE.address.region} {SITE.address.postal}</p>
      </div>
      <div className="flex items-start gap-3 mb-6">
        <Clock size={18} className="text-[#B84A3B] mt-0.5 flex-shrink-0" />
        <div className="text-sm space-y-0.5">
          <p>Mon: 2:00 PM – 7:00 PM</p>
          <p>Tue – Fri: 11:30 AM – 7:00 PM</p>
          <p>Sat &amp; Sun: 11:30 AM – 6:00 PM</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <a href={`tel:${SITE.phone}`} onClick={() => trackContact("phone")} data-testid="landing-call" className="inline-flex items-center gap-2 border border-[#3a3a3a] px-4 py-2.5 text-[0.7rem] font-mono tracking-[0.12em] uppercase hover:bg-[#B84A3B] hover:border-[#B84A3B] transition-colors">
          <Phone size={14} /> Call
        </a>
        <a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Kurti Connection! I'd like some help choosing an outfit.")}`} target="_blank" rel="noreferrer" onClick={() => trackContact("whatsapp")} data-testid="landing-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] text-[#0b3d1e] px-4 py-2.5 text-[0.7rem] font-mono tracking-[0.12em] uppercase hover:opacity-90 transition-opacity">
          <MessageCircle size={14} /> WhatsApp
        </a>
        <a href={`https://maps.google.com/?q=${encodeURIComponent(`${SITE.address.street}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postal}`)}`} target="_blank" rel="noreferrer" onClick={() => trackContact("directions")} data-testid="landing-directions" className="inline-flex items-center gap-2 border border-[#3a3a3a] px-4 py-2.5 text-[0.7rem] font-mono tracking-[0.12em] uppercase hover:bg-[#B84A3B] hover:border-[#B84A3B] transition-colors">
          Directions
        </a>
        <a href={SITE.doordash} target="_blank" rel="noopener noreferrer" onClick={() => trackContact("doordash")} data-testid="landing-doordash" className="inline-flex items-center gap-2 bg-[#EB1700] text-[#FFFFFF] px-4 py-2.5 text-[0.7rem] font-mono tracking-[0.12em] uppercase hover:opacity-90 transition-opacity">
          <Zap size={14} /> Same-Day Delivery
        </a>
      </div>
    </div>
    <div className="overflow-hidden border border-[#2a2a2a] min-h-[220px]">
      <iframe
        title="Kurti Connection store location"
        src={`https://www.google.com/maps?q=${encodeURIComponent(`${SITE.address.street}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postal}`)}&output=embed`}
        width="100%" height="100%" style={{ border: 0, minHeight: 220 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  </div>
);

export default function Landing() {
  const { slug } = useParams();
  const cfg = getLanding(slug);
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!cfg) return;
    api.get(`/products?collection=${cfg.collection}&limit=8`).then((r) => setProducts(r.data.slice(0, 8))).catch(() => {});
  }, [slug, cfg]);

  const jsonLd = useMemo(() => {
    if (!cfg) return [];
    const schemas = [
      breadcrumbSchema([{ name: "Home", path: "/" }, { name: cfg.h1, path: `/${slug}` }]),
      faqSchema(cfg.faqs),
    ];
    if (cfg.local) schemas.push(localBusinessSchema());
    return schemas;
  }, [cfg, slug]);

  if (!cfg) return <Navigate to="/" replace />;
  const onAdd = (product, size) => addItem(product, size);

  return (
    <main data-testid="landing-page">
      <Seo title={cfg.seoTitle} titleRaw description={cfg.metaDescription} path={`/${slug}`} image={cfg.hero} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="bg-[#1A1A1A] text-[#FFFFFF]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
          <div className="flex flex-col justify-center px-5 sm:px-10 py-14 lg:py-20">
            <div className="overline text-[#B84A3B] mb-4">{cfg.kicker}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" data-testid="landing-h1">{cfg.h1}</h1>
            <p className="mt-6 text-base sm:text-lg text-[#FFFFFF]/80 font-body max-w-xl leading-relaxed">{cfg.intro[0]}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/shop" data-testid="landing-cta-shop" className="inline-flex items-center gap-2 bg-[#FFFFFF] text-[#1A1A1A] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] hover:text-white transition-colors">
                Shop Now <ArrowRight size={14} />
              </Link>
              <a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Kurti Connection! I have a question.")}`} target="_blank" rel="noreferrer" data-testid="landing-cta-whatsapp" className="inline-flex items-center gap-2 border border-[#FFFFFF]/30 px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#FFFFFF] hover:text-[#1A1A1A] transition-colors">
                <MessageCircle size={14} /> WhatsApp Us
              </a>
            </div>
          </div>
          <div className="relative min-h-[280px] lg:min-h-full overflow-hidden">
            <img src={cfg.hero} alt={cfg.h1} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
          </div>
        </div>
      </section>

      {/* Quick category links */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8 border-b border-[#E5E5DF]">
        <div className="flex flex-wrap gap-3">
          {cfg.quickLinks.map((q) => (
            <Link key={q.to} to={q.to} data-testid={`landing-quicklink-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="inline-flex items-center gap-2 border border-[#E5E5DF] px-5 py-2.5 text-[0.72rem] font-mono tracking-[0.12em] uppercase text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FFFFFF] hover:border-[#1A1A1A] transition-colors">
              {q.label} <ArrowRight size={13} />
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      {products.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14">
          <Reveal><h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">Handpicked for You</h2></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10">
            {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onAdd={onAdd} />)}
          </div>
        </section>
      )}

      {/* Body copy */}
      <section className="max-w-[820px] mx-auto px-5 sm:px-8 py-6">
        {cfg.intro.slice(1).map((p, i) => (
          <p key={i} className="font-body text-[#333] leading-relaxed mb-4">{p}</p>
        ))}
      </section>

      {/* Local store */}
      {cfg.local && (
        <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12">
          <StoreCard />
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-[820px] mx-auto px-5 sm:px-8 py-12" data-testid="landing-faqs">
        <Reveal><h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-6">Frequently Asked Questions</h2></Reveal>
        {cfg.faqs.map((f, i) => <Faq key={i} q={f.q} a={f.a} idx={i} />)}
      </section>

      {/* Final CTA */}
      <section className="bg-[#B84A3B] text-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Find Your Outfit Today</h2>
            <p className="mt-2 text-white/85 font-body">Shop online or visit our Schaumburg boutique near Chicago.</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/shop" className="inline-flex items-center gap-2 bg-white text-[#B84A3B] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors">
              Shop Now <ArrowRight size={14} />
            </Link>
            <a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/50 px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-white hover:text-[#B84A3B] transition-colors">
              <MessageCircle size={14} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
